import * as _ from 'lodash'

/** handle required for save/publish
 * @param {string|requiredSave|requiredPublish} - requiredKey, this matches dyamo settings obj schema
 * @param {object} settings - v2 settings
 * @param {object} story
 * @param {object} templateConfig - base templates
 * @return {array<object>} - returns array of fields with missing prop
 *   @prop {string} label
 *   @prop {boolean} missing
 *   @prop {string} key
 *   @prop {string} tab
 */
export default function handleRequired(
  requiredKey,
  settings,
  story,
  templateConfig
) {
  if (process.env.NODE_ENV === 'testing') {
    const props = ['requiredSave', 'requiredPublish']
    if (props.indexOf(requiredKey) < 0) {
      console.warn(
        `handleRequired first param must be one of ${props.toString()}`
      )
      return []
    }
  }

  if (!settings) {
    throw new Error(
      'Unable to retrieve settings from Redux store when handling required fields'
    )
  }

  const requiredObj = Object
    // get planning, metas from v2 obj
    .keys(settings)
    // accumulate then into single array and give tab to each obj
    .reduce(
      (acc, tab) => [...acc, ...settings[tab].map((o) => ({ ...o, tab }))],
      []
    )
    // use isValid check and requiredKey check
    .reduce((acc, o) => {
      // check for template that isn't present in config (should not be a prod issue)
      const template = templateConfig[o.key]
      if (!template) {
        return acc
      }

      const obj = {
        tab: o.tab,
        key: o.key,
        label: template.enLabel || template.label,
      }
      const isRequired = (o.settings || {})[requiredKey]

      if (!isRequired) {
        const isIncomplete = _.isFunction(template.isIncomplete)
          ? template.isIncomplete(story)
          : false
        return [...acc, { ...obj, missing: isIncomplete }]
      }

      const check = _.isFunction(template.isValid)
        ? template.isValid(story)
        : false
      if (isRequired && !check) {
        return [...acc, { ...obj, missing: true }]
      } else {
        return acc
      }
    }, [])

  return requiredObj
}

/** reduce missing fields to boolean
 * @param {array<obj>} tabs - json config
 * @param {object} settings - company settings
 *   @prop {array<string>} metas
 *   @prop {array<string>} planning
 * @param {object} fields - missing fields generated from story service
 *   @prop {boolean} [metafieldkey] - the key of the meta field (not ans path)
 * @return {object} - tab settingsKey with values reduced to booleans
 */
export function reduceMissingFields(tabs, settings, fields) {
  // go through each tab
  return tabs.reduce((acc, { settingsKey }) => {
    // map it once for efficiency
    const simpleSettings = !settings[settingsKey]
      ? null
      : settings[settingsKey].map((o) => o.key)
    // ignore tabs that don't have this feature
    return settingsKey === null
      ? acc
      : {
          ...acc,
          // for each tab, reduce missing fields into boolean and set it to scope
          [settingsKey]: Object.keys(fields).reduce((acc2, f) => {
            const hasField = simpleSettings.indexOf(f) > -1
            // don't do anything if tab does not contain field
            if (!hasField) {
              return acc2
            }
            // if any one is true, reduce to true (will trigger ng-if)
            if (acc2 === true) {
              return true
            }
            // all must be false to reduce to false
            return fields[f]
          }, false),
        }
  }, {})
}
